import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { useMonthOptions } from '../lib/useMonthOptions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, Info, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
    { value: 'lavage', label: 'Lavage' },
    { value: 'produits_nettoyage', label: 'Produits nettoyage' },
    { value: 'lustrage_detailing', label: 'Lustrage / detailing' },
    { value: 'pads_microfibres', label: 'Pads / microfibres' },
    { value: 'cles_piles', label: 'Clés / piles' },
    { value: 'consommables_preparation', label: 'Consommables préparation' },
    { value: 'autre', label: 'Autre' },
];

function fmt(v) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v); }

const now = new Date();
const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

const EMPTY = {
    date_facture: now.toISOString().slice(0, 10),
    mois_affectation: currentMonth,
    categorie: 'autre',
    fournisseur: '',
    numero_facture: '',
    montant_ttc: '',
    taux_tva: 20,
    tva_recuperable: false,
    description: '',
};

export default function FraisMutualiseFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const monthOptions = useMonthOptions();

    const [form, setForm] = useState({ ...EMPTY });
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        if (!isEdit) return;
        axios.get(`${API_URL}/api/frais-mutualises`, { withCredentials: true })
            .then(res => {
                const item = res.data.find(d => d.id === id);
                if (item) {
                    setForm({
                        date_facture: item.date_facture || '',
                        mois_affectation: item.mois_affectation || currentMonth,
                        categorie: item.categorie || 'autre',
                        fournisseur: item.fournisseur || '',
                        numero_facture: item.numero_facture || '',
                        montant_ttc: item.montant_ttc || '',
                        taux_tva: item.taux_tva || 20,
                        tva_recuperable: item.tva_recuperable || false,
                        description: item.description || '',
                    });
                }
            });
    }, [id, isEdit]);

    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    // Preview: fetch distribution info when montant or mois changes
    useEffect(() => {
        const mtc = parseFloat(form.montant_ttc);
        if (!form.mois_affectation || !mtc || mtc <= 0) { setPreview(null); return; }
        const timer = setTimeout(async () => {
            setPreviewLoading(true);
            try {
                const res = await axios.post(`${API_URL}/api/frais-mutualises/preview`, {
                    date_facture: form.date_facture,
                    mois_affectation: form.mois_affectation,
                    categorie: form.categorie,
                    montant_ttc: mtc,
                    taux_tva: parseFloat(form.taux_tva) || 20,
                    tva_recuperable: form.tva_recuperable,
                    description: form.description,
                }, { withCredentials: true });
                setPreview(res.data);
            } catch { setPreview(null); }
            setPreviewLoading(false);
        }, 400);
        return () => clearTimeout(timer);
    }, [form.montant_ttc, form.mois_affectation, form.taux_tva, form.tva_recuperable, form.date_facture, form.categorie, form.description]);

    const handleSave = async () => {
        if (!form.mois_affectation) { toast.error('Mois d\'affectation requis'); return; }
        if (!form.montant_ttc || parseFloat(form.montant_ttc) <= 0) { toast.error('Montant TTC requis'); return; }
        setSaving(true);
        try {
            const payload = { ...form, montant_ttc: parseFloat(form.montant_ttc), taux_tva: parseFloat(form.taux_tva) };
            if (isEdit) {
                await axios.put(`${API_URL}/api/frais-mutualises/${id}`, payload, { withCredentials: true });
                toast.success('Frais mutualisé modifié');
            } else {
                await axios.post(`${API_URL}/api/frais-mutualises`, payload, { withCredentials: true });
                toast.success('Frais mutualisé enregistré');
            }
            navigate('/frais-vehicules');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
        setSaving(false);
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="fm-form-page">
            <button onClick={() => navigate('/frais-vehicules')} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors mb-6" data-testid="fm-form-back">
                <ArrowLeft className="w-4 h-4" />Retour aux frais véhicules
            </button>
            <h1 className="text-xl font-semibold text-gray-900 tracking-tight mb-8">
                {isEdit ? 'Modifier le frais mutualisé' : 'Ajouter un frais mutualisé'}
            </h1>

            <div className="card p-6 sm:p-8 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                    <Field label="Date facture">
                        <Input type="date" value={form.date_facture} onChange={e => set('date_facture', e.target.value)} data-testid="fm-date" />
                    </Field>
                    <Field label="Mois d'affectation">
                        <Select value={form.mois_affectation} onValueChange={v => set('mois_affectation', v)}>
                            <SelectTrigger className="h-10 text-sm" data-testid="fm-mois"><SelectValue /></SelectTrigger>
                            <SelectContent>{monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                    <Field label="Catégorie">
                        <Select value={form.categorie} onValueChange={v => set('categorie', v)}>
                            <SelectTrigger className="h-10 text-sm" data-testid="fm-categorie"><SelectValue /></SelectTrigger>
                            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                    <Field label="Fournisseur">
                        <Input value={form.fournisseur} onChange={e => set('fournisseur', e.target.value)} placeholder="Optionnel" data-testid="fm-fournisseur" />
                    </Field>
                    <Field label="N° facture">
                        <Input value={form.numero_facture} onChange={e => set('numero_facture', e.target.value)} placeholder="Optionnel" data-testid="fm-num-facture" />
                    </Field>
                    <Field label="Montant TTC (€)">
                        <Input type="number" step="0.01" inputMode="decimal" value={form.montant_ttc} onChange={e => set('montant_ttc', e.target.value)} placeholder="600.00" className="font-mono" data-testid="fm-montant" />
                    </Field>
                    <Field label="Taux TVA (%)">
                        <Input type="number" value={form.taux_tva} onChange={e => set('taux_tva', parseFloat(e.target.value) || 20)} data-testid="fm-taux-tva" />
                    </Field>
                    <Field label="TVA récupérable">
                        <Select value={form.tva_recuperable ? 'oui' : 'non'} onValueChange={v => set('tva_recuperable', v === 'oui')}>
                            <SelectTrigger className="h-10 text-sm" data-testid="fm-tva-recup"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent>
                        </Select>
                    </Field>
                    <div className="sm:col-span-2">
                        <Field label="Description">
                            <Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Ex: Lot pads lustrage septembre" data-testid="fm-description" />
                        </Field>
                    </div>
                </div>
            </div>

            {/* Preview card */}
            {preview && (
                <div className={`mt-6 rounded-xl border p-5 ${preview.non_reparti ? 'bg-amber-50 border-amber-200' : 'bg-indigo-50/50 border-indigo-200'}`} data-testid="fm-preview">
                    {preview.non_reparti ? (
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-amber-800">Aucun véhicule vendu sur cette période</p>
                                <p className="text-xs text-amber-600 mt-1">Le frais sera conservé mais ne sera pas réparti tant qu'aucun véhicule ne sera vendu en {form.mois_affectation}.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-start gap-3">
                            <Info className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-indigo-900 mb-2">Aperçu de la répartition</p>
                                {previewLoading ? <Loader2 className="w-4 h-4 animate-spin text-indigo-400" /> : (
                                    <div className="grid grid-cols-3 gap-4 text-center">
                                        <div>
                                            <p className="text-[10px] text-indigo-500 uppercase tracking-wider font-medium mb-0.5">Montant HT à répartir</p>
                                            <p className="text-lg font-semibold text-indigo-900 font-mono">{fmt(preview.total_mutualises_ht_apres)}</p>
                                            {preview.total_mutualises_ht_existant > 0 && <p className="text-[10px] text-indigo-400">dont {fmt(preview.total_mutualises_ht_existant)} existant</p>}
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-indigo-500 uppercase tracking-wider font-medium mb-0.5">Véhicules concernés</p>
                                            <p className="text-lg font-semibold text-indigo-900">{preview.nb_vehicules}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-indigo-500 uppercase tracking-wider font-medium mb-0.5">Impact par véhicule</p>
                                            <p className="text-lg font-semibold text-indigo-900 font-mono">{fmt(preview.quote_part_ht)} HT</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => navigate('/frais-vehicules')} className="h-10 px-5 text-sm">Annuler</Button>
                <Button onClick={handleSave} disabled={saving}
                    className="h-10 px-6 text-sm bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="save-fm-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Enregistrer
                </Button>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs text-gray-400 uppercase tracking-wider font-medium">{label}</Label>
            {children}
        </div>
    );
}
